/**
 * Country display names + fallbacks for the economic-outlook pages.
 *
 * The live per-country config comes from `/api/economic-outlook/countries`
 * via `useCountries()`. This file only holds the build-time fallbacks and
 * the frontend-only short labels (e.g. "US" for chart titles).
 */
import type { CountryIso, Timeframe } from '../types';
import { COUNTRIES_BY_DISPLAY_ORDER, COUNTRY_CONFIG } from '@/lib/config/domains';

const NAMES = COUNTRY_CONFIG as Record<string, { display_name: string }>;

/** Frontend-only short labels; any country not listed uses its long name. */
const SHORT_NAMES: Partial<Record<CountryIso, string>> = { US: 'US' };

/** Long display name, e.g. "United States". */
export function longName(iso: CountryIso): string {
  return NAMES[iso]?.display_name ?? iso;
}

/** Short label for chart titles / toolbars, e.g. "US". */
export function shortName(iso: CountryIso): string {
  return SHORT_NAMES[iso] ?? longName(iso);
}

/** Country order used until the runtime config loads. */
export const DEFAULT_COUNTRY_ORDER: CountryIso[] = [...COUNTRIES_BY_DISPLAY_ORDER];

/** Timeframe fallbacks for a country missing from the runtime config. */
export const FALLBACK_TIMEFRAMES: Timeframe[] = ['1y', '2y'];
export const FALLBACK_DEFAULT_TIMEFRAME: Timeframe = '2y';
